import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeFileUrl } from '@/utils/fileUrl';
import { Button } from '@/components/ui/button';
import WelcomeModal from '@/components/WelcomeModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Train,
  LayoutDashboard,
  FileText,
  Building2,
  Users,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Calendar,
  Truck,
  Award,
  Shield,
  GraduationCap,
  Wrench,
  FolderOpen,
  Atom,
  Flame,
  Package,
  BookOpen,
  HardHat,
  Briefcase,
  Target,
  FileCheck,
  ClipboardCheck,
  AlertTriangle,
  List,
  FolderArchive,
  UserCheck,
  CreditCard,
  Clock
} from 'lucide-react';

const MainLayout = () => {
  const { user, logout, currentFacility, setCurrentFacility, api, billingStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [certification, setCertification] = useState(null);
  const [loadingCertification, setLoadingCertification] = useState(false);

  // Billing helpers
  const isAdmin = user?.role === 'ADMIN';
  const isTrialing = billingStatus?.subscription_status === 'trialing';
  const trialEndAt = billingStatus?.trial_end_at;
  const trialDaysLeft = trialEndAt
    ? Math.max(0, Math.ceil((new Date(trialEndAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  // Welcome modal: show once for Admin/Manager/Operator who haven't dismissed it
  const [welcomeDismissedLocally, setWelcomeDismissedLocally] = useState(false);
  const showWelcome =
    !welcomeDismissedLocally &&
    user &&
    ['ADMIN', 'MANAGER', 'OPERATOR'].includes(user.role) &&
    !user.has_seen_welcome_modal_at;

  // Check if user can view certification (Operator, Manager, Admin - not Technician)
  const canViewCertification = user?.role && ['OPERATOR', 'MANAGER', 'ADMIN'].includes(user.role);
  
  // Check if a specific facility is selected (not "all")
  const isSpecificFacilitySelected = currentFacility && currentFacility !== 'all';

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await api.get('/facilities');
        setFacilities(response.data);
        // Phase 2: honor role-based default. ADMIN/MANAGER default to 'all'
        // (set in AuthContext); only OPERATOR/TECHNICIAN fall back to the
        // first accessible facility if nothing is selected yet.
        if (response.data.length > 0 && !currentFacility) {
          const role = user?.role;
          if (role === 'ADMIN' || role === 'MANAGER') {
            setCurrentFacility('all');
          } else {
            setCurrentFacility(response.data[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch facilities:', error);
      }
    };

    fetchFacilities();
  }, [api, currentFacility, setCurrentFacility, user?.role]);

  // Fetch certification when facility changes
  useEffect(() => {
    const fetchCertification = async () => {
      // Only fetch if:
      // 1. User can view certification
      // 2. A specific facility is selected (not "all")
      if (!canViewCertification || !isSpecificFacilitySelected) {
        setCertification(null);
        return;
      }

      setLoadingCertification(true);
      try {
        const response = await api.get(`/facilities/${currentFacility}/certification`);
        setCertification(response.data);
      } catch (error) {
        console.error('Failed to fetch certification:', error);
        setCertification(null);
      } finally {
        setLoadingCertification(false);
      }
    };

    fetchCertification();
  }, [api, currentFacility, canViewCertification, isSpecificFacilitySelected]);

  // Open document in new tab
  const openDocument = (url) => {
    if (url) {
      window.open(normalizeFileUrl(url), '_blank', 'noopener,noreferrer');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['ADMIN', 'MANAGER', 'OPERATOR'] },
    // Techs see Service Requests only if the per-user permission is on
    // (`can_create_service_requests`). Otherwise the link is hidden and the
    // backend also 403s them.
    {
      to: '/service-requests',
      icon: FileText,
      label: 'Service Requests',
      roles: user?.can_create_service_requests
        ? ['ADMIN', 'MANAGER', 'OPERATOR', 'TECHNICIAN']
        : ['ADMIN', 'MANAGER', 'OPERATOR'],
    },
    { to: '/work-schedules', icon: Calendar, label: 'Work Schedules', roles: ['ADMIN', 'MANAGER', 'OPERATOR', 'TECHNICIAN'] },
    { to: '/vehicles', icon: Truck, label: 'Vehicles', roles: ['ADMIN', 'MANAGER', 'OPERATOR'] },
    { to: '/inventory', icon: Package, label: 'Inventory', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/document-library', icon: FolderArchive, label: 'Document Library', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/customers', icon: UserCheck, label: 'Customers & Contacts', roles: ['ADMIN', 'MANAGER', 'OPERATOR'] },
    { to: '/user-management', icon: Users, label: 'User Management', roles: ['ADMIN', 'MANAGER', 'OPERATOR'] },
    { to: '/facility-management', icon: Building2, label: 'Facility Management', roles: ['ADMIN', 'MANAGER'] },
  ];

  // Training Management collapsible section - expanded with new placeholder sub-pages
  const trainingItems = [
    { to: '/training/dashboard', icon: BookOpen, label: 'Training Dashboard', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/training/hse', icon: HardHat, label: 'HSE', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/training/job-roles', icon: Briefcase, label: 'Job Roles', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/training/function-specific', icon: Target, label: 'Function Specific', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/training/ndt', icon: Atom, label: 'NDT Personnel', roles: ['ADMIN', 'MANAGER', 'OPERATOR'] },
    { to: '/training/welding-personnel', icon: Flame, label: 'Welding Personnel', roles: ['ADMIN', 'MANAGER', 'OPERATOR'] },
    { to: '/training/safety-council', icon: Shield, label: 'Safety Council', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
  ];
  
  const [trainingExpanded, setTrainingExpanded] = useState(() => {
    return location.pathname.startsWith('/training');
  });

  // M-1003 collapsible section - expanded with new placeholder sub-pages
  const m1003Items = [
    { to: '/m1003/b1s', icon: FileCheck, label: 'B1s', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/m1003/audits', icon: ClipboardCheck, label: 'Audits', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/m1003/ncrs', icon: AlertTriangle, label: 'NCRs', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/m1003/mdl', icon: List, label: 'MDL', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/m1003/approved-vendors', icon: UserCheck, label: 'Approved Vendor List', roles: ['ADMIN', 'MANAGER', 'OPERATOR'], comingSoon: true },
    { to: '/m1003/mt-equipment', icon: Wrench, label: 'M & T Equipment', roles: ['ADMIN', 'MANAGER', 'OPERATOR'] },
  ];
  
  const [m1003Expanded, setM1003Expanded] = useState(() => {
    // Auto-expand if on an M-1003 page
    return location.pathname.startsWith('/m1003');
  });

  // Update expansion states when location changes
  useEffect(() => {
    if (location.pathname.startsWith('/m1003')) {
      setM1003Expanded(true);
    }
    if (location.pathname.startsWith('/training')) {
      setTrainingExpanded(true);
    }
  }, [location.pathname]);

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));
  const filteredTrainingItems = trainingItems.filter(item => item.roles.includes(user?.role));
  const showTrainingSection = filteredTrainingItems.length > 0;
  const filteredM1003Items = m1003Items.filter(item => item.roles.includes(user?.role));
  const showM1003Section = filteredM1003Items.length > 0;

  const currentFacilityName =
    currentFacility === 'all'
      ? (user?.role === 'MANAGER' ? 'All My Facilities' : 'All Facilities')
      : (facilities.find(f => f.id === currentFacility)?.name || 'Select Facility');

  return (
    <div className="min-h-screen bg-white flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-[#D3D5D4] transform transition-transform duration-200 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo - Onsite Rail Solutions */}
          <div className="p-4 border-b border-[#D3D5D4]">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <img
                  src="/ors-logo.svg"
                  alt="Onsite Rail Solutions"
                  className="h-8 w-auto max-w-full object-contain flex-shrink-0"
                  style={{ objectFit: 'contain', objectPosition: 'left center' }}
                  srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
                  data-testid="sidebar-logo"
                />
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-[#5A5A5A] hover:text-[#323033] flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Facility Switcher */}
          {facilities.length > 0 && (
            <div className="p-4 border-b border-[#D3D5D4]">
              <label className="text-xs font-medium text-[#5A5A5A] uppercase tracking-wider mb-2 block">
                Current Facility
              </label>
              <Select value={currentFacility || ''} onValueChange={setCurrentFacility}>
                <SelectTrigger className="w-full" data-testid="facility-switcher">
                  <SelectValue placeholder="Select facility">{currentFacilityName}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {user?.role === 'ADMIN' && (
                    <SelectItem value="all" data-testid="facility-option-all">All Facilities</SelectItem>
                  )}
                  {user?.role === 'MANAGER' && (
                    <SelectItem value="all" data-testid="facility-option-all">All My Facilities</SelectItem>
                  )}
                  {facilities.map((facility) => (
                    <SelectItem key={facility.id} value={facility.id}>
                      {facility.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Certification Icons - Only shown when specific facility selected and user can view */}
              {canViewCertification && isSpecificFacilitySelected && certification && (
                <TooltipProvider>
                  <div className="flex items-center gap-2 mt-3" data-testid="sidebar-certification-icons">
                    {certification.certificate && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => openDocument(certification.certificate.file_url)}
                            className="p-2 rounded-md bg-[#005EF8]/10 hover:bg-[#005EF8]/20 transition-colors"
                            data-testid="sidebar-certificate-icon"
                            aria-label="View AAR Certificate"
                          >
                            <Award className="w-4 h-4 text-[#005EF8]" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <p>View AAR Certificate</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {certification.letter && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => openDocument(certification.letter.file_url)}
                            className="p-2 rounded-md bg-[#005EF8]/10 hover:bg-[#005EF8]/20 transition-colors"
                            data-testid="sidebar-letter-icon"
                            aria-label="View Letter of Compliance"
                          >
                            <Shield className="w-4 h-4 text-[#005EF8]" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <p>View Letter of Compliance</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              )}
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
              const linkClass = item.comingSoon 
                ? `sidebar-link sidebar-link-coming-soon ${isActive ? 'sidebar-link-active' : ''}`
                : `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={linkClass}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              );
            })}
            
            {/* Training Management Collapsible Section */}
            {showTrainingSection && (
              <div className="pt-2">
                <button
                  onClick={() => setTrainingExpanded(!trainingExpanded)}
                  className="sidebar-link sidebar-link-inactive w-full"
                  data-testid="nav-training-toggle"
                >
                  <GraduationCap className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left truncate">Training</span>
                  {trainingExpanded ? (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 flex-shrink-0" />
                  )}
                </button>
                {trainingExpanded && (
                  <div className="ml-8 mt-1 space-y-1 border-l border-[#D3D5D4] pl-3">
                    {filteredTrainingItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                      const linkClass = item.comingSoon 
                        ? `sidebar-link sidebar-link-coming-soon ${isActive ? 'sidebar-link-active' : ''}`
                        : `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setSidebarOpen(false)}
                          className={linkClass}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {/* M-1003 Collapsible Section */}
            {showM1003Section && (
              <div className="pt-2">
                <button
                  onClick={() => setM1003Expanded(!m1003Expanded)}
                  className="sidebar-link sidebar-link-inactive w-full"
                  data-testid="nav-m1003-toggle"
                >
                  <FolderOpen className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left truncate">M-1003</span>
                  {m1003Expanded ? (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 flex-shrink-0" />
                  )}
                </button>
                {m1003Expanded && (
                  <div className="ml-8 mt-1 space-y-1 border-l border-[#D3D5D4] pl-3">
                    {filteredM1003Items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                      const linkClass = item.comingSoon 
                        ? `sidebar-link sidebar-link-coming-soon ${isActive ? 'sidebar-link-active' : ''}`
                        : `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`;
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setSidebarOpen(false)}
                          className={linkClass}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{item.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* User menu - Light theme */}
          <div className="p-4 border-t border-[#D3D5D4]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded hover:bg-[#F5F5F5] transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded bg-[#005EF8] flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-[#323033] truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-[#5A5A5A] capitalize">{user?.role?.toLowerCase()}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-[#5A5A5A]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                {['ADMIN', 'MANAGER'].includes(user?.role) && (
                  <DropdownMenuItem onClick={() => navigate('/billing')} data-testid="billing-menu-item">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Billing & Subscription
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout} 
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 focus:bg-red-50" 
                  data-testid="logout-menu-item"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-border px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-[#323033] hover:bg-muted rounded-md"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <img
                src="/ors-logo.svg"
                alt="Onsite Rail Solutions"
                className="h-7 w-auto max-w-full object-contain flex-shrink-0"
                style={{ objectFit: 'contain', objectPosition: 'left center' }}
                srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
                data-testid="mobile-header-logo"
              />
            </div>
            <div className="w-9" /> {/* Spacer for alignment */}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          {/* Trial banner */}
          {isTrialing && trialDaysLeft !== null && (
            <div
              className={`mb-4 px-4 py-2.5 rounded-lg flex items-center justify-between text-sm ${
                trialDaysLeft <= 3
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : trialDaysLeft <= 7
                  ? 'bg-amber-50 border border-amber-200 text-amber-800'
                  : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}
              data-testid="trial-banner"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>
                  <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong> left in your free trial.
                  {trialDaysLeft <= 3 && ' Add a payment method now to avoid losing access.'}
                </span>
              </div>
              {isAdmin && (
                <button
                  onClick={() => navigate('/billing')}
                  className={`ml-4 px-3 py-1 rounded text-xs font-medium whitespace-nowrap ${
                    trialDaysLeft <= 3
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : trialDaysLeft <= 7
                      ? 'bg-amber-600 text-white hover:bg-amber-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  data-testid="trial-banner-upgrade-btn"
                >
                  Upgrade Now
                </button>
              )}
            </div>
          )}
          <Outlet />
        </main>
      </div>

      {/* Welcome modal — one-time for Admin/Manager/Operator */}
      {showWelcome && (
        <WelcomeModal onDismissed={() => setWelcomeDismissedLocally(true)} />
      )}
    </div>
  );
};

export default MainLayout;
